import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { UsersService } from '../../../core/services/users.service';
import { User } from '../../../core/models/user.interface';
import { LoaderService } from '../../../core/shared/services/loader.service';
import { AuthService } from '../../../core/services/auth.service';
import { AuthUser } from '../../../core/models/auth/auth.interface';
import { ToggleSwitchComponent } from "../../../core/shared/components/switch/toggle-switch.component";
import { ConfigurationService } from '../../../core/services/configuration.service';
import { COACH_TYPES, SUBSCRIPTION_TYPES, USER_TYPES } from '../../../core/shared/constants';

@Component({
  selector: 'app-mentors',
  standalone: true,
  imports: [CommonModule, FormsModule, ToggleSwitchComponent],
  templateUrl: './mentors.component.html',
  styleUrl: './mentors.component.scss'
})
export class MentorsComponent implements OnInit {
  mentors: { 
    id: string, 
    name: string, 
    description: string, 
    photo?: string, 
    categoryExpertIds?: number[], // Array de IDs de categorías (nuevo esquema)
    categoryExpertId?: number, // Mantener para compatibilidad con filtrado
    userTypeId: number, 
    categoryExpert?: string, // Primera categoría para mostrar
    categoryExperts?: string[], // Array de nombres de categorías (nuevo esquema)
    expertCost?: number,
    coachProfileData?: {
      experienceSummary?: string;
      professionalDescription?: string;
      professionalTitle?: string;
    }
  }[] = [];
  filteredMentors: { 
    id: string, 
    name: string, 
    description: string, 
    photo?: string, 
    categoryExpertIds?: number[], // Array de IDs de categorías (nuevo esquema)
    categoryExpertId?: number, // Mantener para compatibilidad con filtrado
    userTypeId: number, 
    categoryExpert?: string, // Primera categoría para mostrar
    categoryExperts?: string[], // Array de nombres de categorías (nuevo esquema)
    expertCost?: number,
    coachProfileData?: {
      experienceSummary?: string;
      professionalDescription?: string;
      professionalTitle?: string;
      aboutMentor?: string;
    }
  }[] = [];
  isLoading = true;
  searchTerm: string = '';
  
  // ===== CONFIGURACIÓN DE TIPOS DE USUARIOS A LISTAR =====
  // Comentar/descomentar las líneas según qué tipos de usuarios quieras mostrar
  // Para listar solo Expertos: descomenta la línea de EXPERT y comenta la de COACH
  // Para listar solo Mentores/Coaches: descomenta la línea de COACH y comenta la de EXPERT
  // Para listar ambos: descomenta ambas líneas
  private readonly LIST_EXPERTS = true;   // Descomenta esta línea para listar Expertos
  private readonly LIST_COACHES = false;  // Descomenta esta línea para listar Mentores/Coaches
  
  // userTypeId se usa para compatibilidad con código comentado de cambio de switch
  userTypeId = USER_TYPES.Expert.id;
  private loader = inject(LoaderService);
  private authService: AuthService = inject(AuthService);
  private authUser: AuthUser | null = null;
  private configService = inject(ConfigurationService);

  // selectedCategoryId: number = 1; // ID para 'Todos'
  selectedCategoryId: number = 0; // ID para 'Todos'
  showAllCategories: boolean = true;
  todasLasCategorias: { id: number, name: string }[] = [];
  userTypeEnum = USER_TYPES;

  // Variables para paginación
  currentPage = 1;
  itemsPerPage = 6;
  totalPages = 1;

  constructor(
    private usersService: UsersService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.authUser = this.authService.getAuthUser();
    this.loadCategoriesExperts();
    this.loadMentors();
  }

  // Método comentado para futura implementación de cambio entre Expert y Mentor
  // changeSwitch($event: any) {
  //   this.userTypeId = $event;
  //   this.loadMentors();
  //   console.log('this.typeUserId', this.userTypeId);
  // }

  goToMentorProfile(mentorId: string): void {
    this.router.navigate(['/dashboard/mentor', mentorId]);
  }

  scheduleAppointment(mentorId: string): void {
    // Navegar al perfil del mentor y abrir automáticamente el modal de programación
    this.router.navigate(['/dashboard/mentor', mentorId], {
      queryParams: { openScheduling: 'true' }
    });
  }



  // ***** CATEGORIAS DE EXPERTOS ***** //
  get categorias(): any[] {
    let categoriasFiltradas = this.todasLasCategorias.filter(categoria => {
      // Siempre incluir "Todos" (id: 0) y filtrar el resto por count > 0
      if (categoria.id === 0) {
        return true;
      }
      return this.getCategoryCount(categoria.id) > 0;
    });
    
    return this.showAllCategories ? categoriasFiltradas : categoriasFiltradas.slice(0, 6);
  }

  loadCategoriesExperts() {
    this.configService.getExpertCategories().subscribe({
      next: (response: any) => {
        this.todasLasCategorias = [{ id: 0, name: 'Todos' }]
        this.todasLasCategorias.push(...response.data.map((type: any) => ({
          ...type,
          id: Number(type.id)
        })));
        // this.subscriptionTypes = response.data;
        console.log('Categoría expertos:', this.todasLasCategorias);
      },
      error: (error) => {
        console.error('Error cargando tipos de suscripción:', error);
      }
    });
  }

  toggleShowAllCategories(): void {
    this.showAllCategories = !this.showAllCategories;
  }

  get expertosCount(): number {
    if (!this.filteredMentors) return 0;
    return this.filteredMentors.filter(m => m?.name &&  m.name.trim() !== 'Team Phoenix'.trim() && m.name.trim() !== 'Jaime Aguirre Guarderas'.trim()  ).length;
  }

  // get expertosCount(): number {
  //   return this.filteredMentors.length;
  // }

  getProfilePhotoUrl(mentor: any): string {
    if (mentor?.personalSection?.photoBase64) {
      return mentor.personalSection.photoBase64;
    }
    return mentor?.photo || "assets/img/user-photo-default.png";
  }

  // Función para limitar texto a 150 palabras
  limitTextToWords(text: string | undefined, maxWords: number = 20): string {
    if (!text) return 'Sin descripción disponible';
    
    const words = text.trim().split(/\s+/);
    if (words.length <= maxWords) {
      return text;
    }
    
    const limitedWords = words.slice(0, maxWords);
    return limitedWords.join(' ') + '...';
  }

  // Función para formatear el precio del experto
  formatExpertPrice(cost: number | undefined): string {
    if (!cost) return 'S/0.00';
    return `S/${cost.toFixed(2)}`;
  }

  // Función para obtener las categorías restantes (todas excepto la primera)
  getRemainingCategories(categories: string[]): string {
    if (!categories || categories.length <= 1) return '';
    return categories.slice(1).join(', ');
  }



  // Métodos de paginación
  get displayedMentors() {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    return this.filteredMentors.slice(startIndex, endIndex);
  }

  get totalItems(): number {
    return this.filteredMentors.length;
  }

  getDisplayedItemsText(): string {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage + 1;
    const endIndex = Math.min(this.currentPage * this.itemsPerPage, this.totalItems);
    return `${startIndex}-${endIndex}`;
  }

  previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
    }
  }

  goToPage(page: number): void {
    this.currentPage = page;
  }

  onItemsPerPageChange(): void {
    this.currentPage = 1;
    this.calculateTotalPages();
  }

  private calculateTotalPages(): void {
    this.totalPages = Math.ceil(this.totalItems / this.itemsPerPage);
  }

  getVisiblePages(): number[] {
    const pages: number[] = [];
    const maxVisiblePages = 5;
    
    if (this.totalPages <= maxVisiblePages) {
      // Si hay 5 páginas o menos, mostrar todas
      for (let i = 1; i <= this.totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Lógica para mostrar páginas alrededor de la página actual
      let startPage = Math.max(1, this.currentPage - 2);
      let endPage = Math.min(this.totalPages, this.currentPage + 2);
      
      // Ajustar para mostrar siempre 5 páginas cuando sea posible
      if (endPage - startPage < maxVisiblePages - 1) {
        if (startPage === 1) {
          endPage = Math.min(this.totalPages, startPage + maxVisiblePages - 1);
        } else if (endPage === this.totalPages) {
          startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }
      }
      
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
    }
    
    return pages;
  }

  getCategoryCount(categoryId: number): number {
    if (categoryId === 0) {
      return this.mentors.length;
    }
    // Filtrar por categoría: verificar si el mentor tiene la categoría en su array de categorías
    return this.mentors.filter(mentor => {
      // Verificar en el nuevo esquema (array de IDs)
      if (mentor.categoryExpertIds && mentor.categoryExpertIds.length > 0) {
        return mentor.categoryExpertIds.includes(categoryId);
      }
      // Compatibilidad con el esquema antiguo (categoría única)
      return mentor.categoryExpertId === categoryId;
    }).length;
  }

  // Actualizar el método selectCategory para recalcular páginas
  selectCategory(categoria: any): void {
    this.selectedCategoryId = categoria ? categoria.id : 0;
    this.currentPage = 1; // Reset a la primera página
    
    const term = this.searchTerm.toLowerCase().trim();
    if (this.selectedCategoryId === 0) {
      this.filteredMentors = this.mentors.filter(mentor =>
        mentor.name.toLowerCase().includes(term) ||
        mentor.description.toLowerCase().includes(term)
      );
    } else {
      this.filteredMentors = this.mentors.filter(mentor => {
        const matchesSearch = mentor.description.toLowerCase().includes(term) || mentor.name.toLowerCase().includes(term);
        // Verificar en el nuevo esquema (array de IDs)
        const matchesCategory = mentor.categoryExpertIds && mentor.categoryExpertIds.includes(this.selectedCategoryId) ||
                                // Compatibilidad con el esquema antiguo (categoría única)
                                mentor.categoryExpertId === this.selectedCategoryId;
        return matchesSearch && matchesCategory;
      });
    }
    
    this.calculateTotalPages();
    console.log('this.filteredMentors', this.filteredMentors);
  }

  // Actualizar el método searchMentors para recalcular páginas
  searchMentors(): void {
    this.currentPage = 1; // Reset a la primera página
    const term = this.searchTerm.toLowerCase().trim();
    
    if (this.selectedCategoryId === 0) {
      this.filteredMentors = this.mentors.filter(mentor =>
        mentor.name.toLowerCase().includes(term) ||
        mentor.description.toLowerCase().includes(term)
      );
    } else {
      this.filteredMentors = this.mentors.filter(mentor => {
        const matchesSearch = mentor.description.toLowerCase().includes(term) || mentor.name.toLowerCase().includes(term);
        // Verificar en el nuevo esquema (array de IDs)
        const matchesCategory = mentor.categoryExpertIds && mentor.categoryExpertIds.includes(this.selectedCategoryId) ||
                                // Compatibilidad con el esquema antiguo (categoría única)
                                mentor.categoryExpertId === this.selectedCategoryId;
        return matchesSearch && matchesCategory;
      });
    }
    
    this.calculateTotalPages();
  }

  // Actualizar el método loadMentors para recalcular páginas
  // Carga usuarios según la configuración (Expert, Coach, o ambos)
  loadMentors() {
    this.isLoading = true;
    this.loader.show();
    
    // Determinar qué tipos de usuarios buscar según la configuración
    const userTypesToLoad: number[] = [];
    if (this.LIST_EXPERTS) {
      userTypesToLoad.push(USER_TYPES.Expert.id);
    }
    if (this.LIST_COACHES) {
      userTypesToLoad.push(USER_TYPES.Coach.id);
    }
    
    // Si no hay tipos configurados, usar Expert por defecto
    if (userTypesToLoad.length === 0) {
      userTypesToLoad.push(USER_TYPES.Expert.id);
    }
    
    // Si solo hay un tipo, hacer una sola llamada
    if (userTypesToLoad.length === 1) {
      this.loadUsersByType(userTypesToLoad[0]);
    } else {
      // Si hay múltiples tipos, cargar ambos y combinar resultados
      let allUsers: any[] = [];
      let completedRequests = 0;
      const totalRequests = userTypesToLoad.length;
      
      userTypesToLoad.forEach(typeId => {
        this.usersService.find(typeId, "").subscribe({
          next: (response) => {
            if (response && response.data) {
              allUsers = allUsers.concat(response.data);
            }
            completedRequests++;
            
            // Cuando todas las peticiones terminen, procesar los resultados
            if (completedRequests === totalRequests) {
              this.processUsers(allUsers);
            }
          },
          error: (error) => {
            console.error(`Error cargando usuarios tipo ${typeId}:`, error);
            completedRequests++;
            
            // Si todas las peticiones terminen (incluso con errores), procesar los resultados
            if (completedRequests === totalRequests) {
              this.processUsers(allUsers);
            }
          }
        });
      });
    }
  }

  // Método auxiliar para cargar usuarios de un solo tipo
  private loadUsersByType(typeId: number): void {
    this.usersService.find(typeId, "").subscribe({
      next: (response) => {
        if (response && response.data) {
          this.processUsers(response.data);
        } else {
          this.mentors = [];
          this.filteredMentors = [];
          this.calculateTotalPages();
          this.isLoading = false;
          this.loader.hide();
        }
      },
      error: (error) => {
        console.error('Error cargando usuarios:', error);
        this.isLoading = false;
        this.loader.hide();
      }
    });
  }

  // Método auxiliar para procesar usuarios (filtrado y mapeo)
  private processUsers(users: any[]): void {
    let filteredUsers = users;
    
    // Filtrar usuarios según los tipos configurados
    filteredUsers = filteredUsers.filter(user => {
      if (!user.type) return false;
      
      // Si solo se listan Expertos
      if (this.LIST_EXPERTS && !this.LIST_COACHES) {
        return user.type.id === USER_TYPES.Expert.id;
      }
      // Si solo se listan Coaches
      if (this.LIST_COACHES && !this.LIST_EXPERTS) {
        return user.type.id === USER_TYPES.Coach.id;
      }
      // Si se listan ambos
      if (this.LIST_EXPERTS && this.LIST_COACHES) {
        return user.type.id === USER_TYPES.Expert.id || user.type.id === USER_TYPES.Coach.id;
      }
      
      return false;
    });

    // Filtrado adicional por tipo de Coach/Mentor (solo aplica si LIST_COACHES está activo)
    if (this.LIST_COACHES && !this.LIST_EXPERTS) {
      var filterCoachTypeId =
        this.authUser?.type?.suscriptionType?.id == SUBSCRIPTION_TYPES.Executive.id ? COACH_TYPES.Executive.id :
          this.authUser?.type?.suscriptionType?.id == SUBSCRIPTION_TYPES.Professional.id ? COACH_TYPES.Professional.id : 0;

      filteredUsers = filteredUsers.filter(user => user.type
        && user.type.id === USER_TYPES.Coach.id
        && user.type.coachType?.id == filterCoachTypeId.toString()
      );
    }

    // Excluir al usuario actual de la lista
    if (this.authUser) {
      filteredUsers = filteredUsers.filter(user => {
        const userId = user.id || (user as any)._id;
        return userId !== this.authUser!.id;
      });
    }

    // Filtrar solo usuarios con publicado = true
    // Solo mostrar usuarios que tengan explícitamente publicado = true
    filteredUsers = filteredUsers.filter(user => {
      return user.publicado === true;
    });

    // Mapear los usuarios (expertos y/o coaches) con el nuevo esquema de categorías múltiples
    this.mentors = filteredUsers.map(user => {
      // Obtener categorías del nuevo esquema (array) o del antiguo (singular)
      // Nota: Solo Expertos tienen categorías, los Coaches no
      let categoryExpertIds: number[] = [];
      let categoryExperts: string[] = [];
      let categoryExpert: string | undefined;
      let expertCost: number | undefined;
      let categoryExpertId: number | undefined; // Para compatibilidad

      // Solo procesar categorías si es un Experto
      if (user.type.id === USER_TYPES.Expert.id) {
        if (user.type.expertCategories && Array.isArray(user.type.expertCategories) && user.type.expertCategories.length > 0) {
          // Nuevo esquema: array de categorías
          categoryExpertIds = user.type.expertCategories.map((cat: any) => Number(cat.id));
          categoryExperts = user.type.expertCategories.map((cat: any) => cat.name);
          categoryExpert = categoryExperts[0]; // Primera categoría para mostrar
          expertCost = Number(user.type.expertCategories[0].cost);
          categoryExpertId = categoryExpertIds[0]; // Para compatibilidad con filtrado
        } else if (user.type.expertCategory) {
          // Esquema antiguo: categoría única (compatibilidad)
          categoryExpertId = Number(user.type.expertCategory.id);
          categoryExpertIds = [categoryExpertId];
          const categoryName = user.type.expertCategory.name || '';
          categoryExpert = categoryName;
          categoryExperts = [categoryName];
          expertCost = Number(user.type.expertCategory.cost);
        }
      }

      return {
        id: user.id || (user as any)._id || '',
        name: user.name || '',
        description: user.professionalSection?.skills || 'Sin habilidades especificadas',
        photo: user.photo || user.personalSection?.photoBase64 || '',
        categoryExpertIds: categoryExpertIds.length > 0 ? categoryExpertIds : undefined,
        categoryExpertId: categoryExpertId, // Para compatibilidad con filtrado
        categoryExpert: categoryExpert, // Primera categoría para mostrar
        categoryExperts: categoryExperts.length > 0 ? categoryExperts : undefined, // Todas las categorías
        expertCost: expertCost,
        userTypeId: user.type.id,
        coachProfileData: {
          experienceSummary: user.coachProfileData?.experienceSummary || user.professionalSection?.skills || 'Sin descripción disponible',
          professionalDescription: user.coachProfileData?.professionalDescription || user.professionalSection?.skills || 'Sin descripción profesional disponible',
          aboutMentor: user.coachProfileData?.aboutMentor || 'Sin descripción sobre mi disponible',
          professionalTitle: user.coachProfileData?.professionalTitle || 'Sin título profesional'
        }
      };
    });

    this.filteredMentors = [...this.mentors];
    this.calculateTotalPages();
    this.isLoading = false;
    this.loader.hide();
  }

}
